import { Alert, Button, Grid, List, ListItem, ListItemButton, Typography } from "@mui/material";
import { SEARCH_DELAY_DEBOUNCE } from "app/appConstants";
import { MprcRequestErrorReport, MprcSpinnerIndicator } from "components";
import { IconButtonClearSearch } from "components/iconButtons/IconButtonClearSearch";
import { TextFieldFilter } from "components/MprcAdvFilter";
import { MprcBoxDrawer } from "components/MprcBoxDrawer";
import { SubtitleItemDto } from "generated/CoreApi_v1";
import { Formaters } from "lib/Formaters";
import { isCanceledError, useCoreApi } from "providers/CoreApiProvider";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SessionService } from "services/sessionService";
import { publishCardVideoSeekEvent } from "views/knowledge/components/card/CardEvents";

////////////////////////////////////////////////////////////////////////////////

function markText(text: string, searchText: string) {
    if (text.length == 0 || searchText.length == 0) {
        return text;
    }

    // Destaca as palavras e/ou expressões.
    const reSplit = new RegExp('[\"].+?[\"]|[^ ]+', "ig");
    const words = searchText.match(reSplit);
    words?.forEach((w) => {
        const w1 = Formaters.getAccentInsensitiveWords(w.replace(/\"/g, ""));
        const regex = new RegExp(`(${w1}[.,;!?"']?)`, "ig");
        text = text.replace(regex, '<b>$1</b>');
    });
    return text.replace(/\n/g, "<br/>");
}

////////////////////////////////////////////////////////////////////////////////

interface IPros {
    attachId: string;
    title?: string;
    searchText?: string;
    onClose: () => void;
}

interface VideoTranscriotionResultsProps {
    attachId: string;
    searchText: string;
    searching: boolean;
    onCompleteSearch: (resultsCount: number) => void;
}

export const VideoTranscriptionResults: React.FC<VideoTranscriotionResultsProps> = ({ attachId, searchText, searching, onCompleteSearch }) => {

    const api = useCoreApi();
    const { t } = useTranslation("knowledge");

    const viewData = useRef<SubtitleItemDto[] | null>(null);
    const [loading, setLoading] = useState<boolean>(false);

    const abortControllerRef = useRef<AbortController | undefined>(undefined);
    const [viewError, setViewError] = useState<any>();


    async function loadData() {
        try {
            setLoading(true);

            abortControllerRef.current = new AbortController();
            var result = await api.v1.knowledgePublication.getAttachTranscription(
                {
                    attachId,
                    searchText: searchText!
                },
                { signal: abortControllerRef.current.signal }
            );
            viewData.current = result.data;

            setLoading(false);
            onCompleteSearch(result.data.length);
        }
        catch (e) {
            if (!isCanceledError(e)) {
                setViewError(e);
                viewData.current = null;
            }
            setLoading(false);
            onCompleteSearch(0);
        }
        finally {
            abortControllerRef.current = undefined;
        }
    }

    useEffect(() => {
        if (searching) loadData();
    }, [searching]);

    return (
        <>
            <MprcRequestErrorReport error={viewError} />
            {
                loading
                    ? <MprcSpinnerIndicator />
                    : <>
                        {
                            viewData.current && viewData.current.length === 0 &&
                            <Alert severity="warning">{t("Feed.Card.Actions.VideoTranscriptionDrawer.ListEmptyMessage")} </Alert>
                        }

                        {
                            viewData.current && viewData.current.length > 0 &&
                            <List disablePadding>
                                {
                                    viewData.current.map((v, index) => {
                                        var seekTime = Math.trunc(v.startTime / 1000);

                                        return (
                                            <ListItem disablePadding key={index}>
                                                <ListItemButton disableGutters onClick={() => publishCardVideoSeekEvent({ videoId: attachId, seekTime: seekTime })}>
                                                    <Grid container spacing={1}>
                                                        <Grid>
                                                            <Typography variant="subtitle1" component="div" color="info.main"><small>{v.startTimeFormated}</small></Typography>
                                                        </Grid>
                                                        <Grid size="grow">
                                                            <Typography variant="subtitle1" component="div" color="muted.main"><span dangerouslySetInnerHTML={{ __html: markText(v.lines, searchText) }}></span></Typography>
                                                        </Grid>
                                                    </Grid>
                                                </ListItemButton>
                                            </ListItem>
                                        );
                                    })
                                }
                            </List>
                        }
                    </>
            }
        </>
    );
};

interface VideoTranscriptionSearchFieldProps {
    autoFocus: boolean;
    searchText?: string;
    inputRef?: React.Ref<any>;
    onConfirm: (value: string) => void;
}

export const VideoTranscriptionSearchField = (props: VideoTranscriptionSearchFieldProps) => {

    const { autoFocus, onConfirm, inputRef } = props;
    const { t } = useTranslation("knowledge");

    const [searchText, setSearchText] = useState<string>(props.searchText ?? "");
    const [lastSearchText, setLastSearchText] = useState<string>("");

    const abortControllerRef = useRef<AbortController | undefined>(undefined);

    const handleSearch = () => {
        onConfirm(searchText);
        setLastSearchText(searchText);
    };

    /// Executa a pesquisa depois de 600ms.
    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            abortControllerRef.current?.abort();
            handleSearch();
        }, SEARCH_DELAY_DEBOUNCE);

        return () => {
            clearTimeout(delayDebounce);
            abortControllerRef.current?.abort();
        };
    }, [searchText]);

    return (
        <TextFieldFilter
            autoFocus={autoFocus}
            fullWidth
            autoComplete="off"
            placeholder={t("Feed.Card.Actions.VideoTranscriptionDrawer.SearchTextPlaceholder")}
            value={searchText}
            onKeyDown={(e) => e.keyCode == 13 && searchText.length > 0 && searchText != lastSearchText && handleSearch()}
            onChange={(e) => setSearchText(e.target.value ?? "")}
            inputRef={inputRef}
            slotProps={{
                input: {
                    endAdornment:
                        searchText != "" &&
                        <IconButtonClearSearch
                            size="small"
                            onClick={() => {
                                setSearchText("");
                                onConfirm("");
                            }}
                        />
                }
            }}
        />
    );
};

export const VideoTranscriptionDrawer = (props: IPros) => {

    const { attachId, title, onClose } = props;
    const { t } = useTranslation("knowledge");

    const [searching, setSearching] = useState<boolean>(false);
    const [searchText, setSearchText] = useState<string>(props.searchText ?? "");
    const [resultCount, setResultsCount] = useState<number>(0);

    const searchTextRef = useRef<HTMLInputElement>(undefined);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////


    const handleDownload = useCallback(() => {
        SessionService.downloadWithToken(`/api/v1/knowledgePublication/ExportAttachTranscription?attachId=${attachId}`, `${title}.txt`);
    }, []);

    useEffect(() => {
        // Quando o menu de transbordo precisa de SCROLL para ser visto,
        // ao visualizar a Transcrição, o vídeo pode ficar "cortado".
        // Para corrigir, ao exibir o DRAWER, movemos o scroll para a posição (0,0).
        // var doc = document.getElementById(INSIDE_LAYOUT_CONTENT_CONTAINER_ID);
        // if (doc) doc.scrollTo(0, 0);
    }, []);

    return (
        <MprcBoxDrawer
            onClose={() => onClose()}
            title={t("Feed.Card.Actions.VideoTranscriptionDrawer.Title")}
            subtitle={title}
            header={
                <VideoTranscriptionSearchField
                    searchText={props.searchText}
                    inputRef={searchTextRef}
                    autoFocus
                    onConfirm={(value) => {
                        setSearchText(value);
                        setSearching(true);
                    }}
                />
            }
            content={
                <VideoTranscriptionResults
                    searching={searching}
                    onCompleteSearch={(count) => {
                        setResultsCount(count);
                        setSearching(false);
                        setTimeout(() => searchTextRef.current?.focus(), 100);
                    }}
                    attachId={attachId}
                    searchText={searchText}
                />
            }
            footer={
                <Button onClick={handleDownload} variant="contained" disabled={resultCount == 0}>
                    {t("Feed.Card.Actions.VideoTranscriptionDrawer.DownloadAllButtonText")}
                </Button>
            }
        />
    );
};
